import { useMemo } from "react"

import { Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Icon from "../../../../Icon"

import styles from "./winRateGrid.module.less"

// Continuous blue heat: win rate -> brand-blue intensity. Data-driven colour, so
// it's computed here (an inline style, like the Sankey ribbon widths) rather than
// a Less class. The ramp floor is theme-keyed: a flat 0.14 tile sinks into the
// dark surface (~#141414, the page and card share the same bg), so the dark floor
// is lifted to keep low-win-rate cells reading as blue. The number ink is then
// chosen by the tile's composited luminance, so contrast holds at every step in
// both themes. Empty pairs (no resolved disputes) render as a faint dash cell.
const BRAND = [34, 90, 234] // @bluePrimary
const SURFACE_BG = { light: [255, 255, 255], dark: [20, 20, 20] }

// WCAG relative luminance of an rgb triplet.
const channel = (c) => {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}
const luminance = ([r, g, b]) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
// Composite a translucent brand tile over the surface to judge the colour the eye sees.
const over = (alpha, bg) => BRAND.map((c, i) => Math.round(c * alpha + bg[i] * (1 - alpha)))

const heatStyle = (winRate, dark) => {
  if (winRate == null) {
    return {
      background: dark ? "rgba(255, 255, 255, 0.04)" : "#F4F4F5",
      color: dark ? "#52525B" : "#A1A1AA"
    }
  }
  const base = dark ? 0.3 : 0.14
  const opacity = base + (1 - base) * (winRate / 100)
  const tile = over(opacity, dark ? SURFACE_BG.dark : SURFACE_BG.light)
  // White ink on the deeper tiles, Neutral800 ink on the paler ones, picked by the
  // composited luminance so the figure never washes out at the mid of the scale.
  const color = luminance(tile) < 0.3 ? "#FFFFFF" : "#27272A"
  return {
    background: `rgba(34, 90, 234, ${opacity.toFixed(3)})`,
    color
  }
}

// Shared "Win rate: category x network" heat grid. Rows = reason categories, columns
// = card schemes; each cell is shaded by its win rate (won / resolved for that pair)
// and, on hover, a tooltip spells out the exact figures. A dash means no resolved
// disputes for that pair; the gradient legend explains lower -> higher. Used by the
// compact analytics card (top categories) and the full inner page (every category).
const WinRateGrid = ({ cols, rows }) => {
  const { t } = useTranslation()
  const dark = useSelector((s) => s.nav.theme) === "dark"
  const colLabel = useMemo(
    () =>
      cols.reduce((m, c) => {
        m[c.key] = c.label
        return m
      }, {}),
    [cols]
  )

  const cellTitle = (rowLabel, col, cell) => (
    <div>
      <div style={{ fontWeight: 500 }}>{`${rowLabel} · ${colLabel[col]}`}</div>
      <div style={{ marginTop: 4 }}>
        {cell.winRate == null
          ? t("No data")
          : `${t("_rate_% win rate", { rate: Math.round(cell.winRate) })} · ${t("_won_ of _resolved_ resolved", { won: cell.won, resolved: cell.resolved })}`}
      </div>
    </div>
  )

  return (
    <>
      <div className={styles.wrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.rowHead} />
              {cols.map((c) => (
                <th key={c.key} aria-label={c.label}>
                  <span className={styles.head} title={c.label}>
                    <Icon type={c.icon} />
                    <span className={styles.headName}>{c.short || c.label}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td className={styles.rowHead}>{row.label}</td>
                {row.cells.map((cell) => (
                  <td key={cell.col}>
                    <Tooltip
                      title={cellTitle(row.label, cell.col, cell)}
                      placement="top"
                      mouseEnterDelay={0.1}
                    >
                      <div className={styles.cell} style={heatStyle(cell.winRate, dark)}>
                        {cell.winRate == null ? "-" : `${Math.round(cell.winRate)}%`}
                      </div>
                    </Tooltip>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.legend}>
        <span>{t("Lower win rate")}</span>
        <span className={styles.scale} />
        <span>{t("Higher win rate")}</span>
      </div>
    </>
  )
}

export default WinRateGrid
